import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { UploadCloud, Link2, FileText } from 'lucide-react';
import { api, errMsg, pad } from '../../lib/api';

const box = 'rounded-2xl border border-white/[0.08] bg-[#060a14]/60 p-7';
const inputCls = 'w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-amber-400/60 transition-colors';

export default function ImportPanel({ docs, onDone }) {
  const [kind, setKind] = useState('doctrine');
  const [category, setCategory] = useState('manuscript');
  const [target, setTarget] = useState('');
  const [publish, setPublish] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const query = target ? `?target_id=${target}` : '';

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('publish', publish);
    if (kind === 'doctrine') {
      if (target) fd.append('target_id', target);
    } else {
      fd.append('category', category);
    }
    try {
      const r = await api.post(kind === 'doctrine' ? '/admin/doctrines/import' : '/admin/documents/import', fd);
      toast.success(`Imported "${r.data.title}" · ${r.data.word_count} words${r.data.verbatim_verified ? ' · verbatim verified' : ''}`);
      onDone(r.data, kind);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const importUrl = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/admin/doctrines/import-url${query}`, { url, publish });
      toast.success(`Imported "${r.data.title}" · ${r.data.word_count} words`);
      setUrl('');
      onDone(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-panel">
      <div className={box}>
        <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Destination</p>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="import-kind-toggle">
          {[['doctrine', 'Doctrine'], ['document', 'Manuscript / Thesis / Novel / Paper']].map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} className={`font-mono-precision text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${kind === k ? 'border-amber-400/60 text-amber-300' : 'border-white/10 text-slate-500 hover:text-slate-300'}`} data-testid={`import-kind-${k}`}>{l}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
          {kind === 'doctrine' ? (
          <label className="block">
            <span className="text-xs text-slate-400">Fill an existing doctrine, or create a new one</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className={`${inputCls} mt-2`} data-testid="import-target-select">
              <option value="">＋ Create new doctrine</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{pad(d.number)} — {d.title} ({d.status})</option>)}
            </select>
          </label>
          ) : (
          <label className="block">
            <span className="text-xs text-slate-400">Publication type — each file becomes its own standalone page</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} mt-2`} data-testid="import-category-select">
              {['manuscript', 'thesis', 'novel', 'paper', 'note', 'transcript', 'source', 'reference'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          )}
          <label className="flex items-center gap-3 pb-2.5 cursor-pointer">
            <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-amber-400 w-4 h-4" data-testid="import-publish-checkbox" />
            <span className="text-xs text-slate-300">Publish immediately</span>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div
          className={`${box} border-dashed text-center transition-colors ${drag ? 'border-amber-400/60 bg-amber-400/[0.04]' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); importFile(e.dataTransfer.files[0]); }}
          data-testid="import-dropzone"
        >
          <UploadCloud size={28} className="mx-auto text-amber-300" strokeWidth={1.5} />
          <p className="font-celestial text-lg text-slate-100 mt-4">Drop a file</p>
          <p className="text-xs text-slate-500 mt-2">.docx · .pdf · .md · .txt · .html — up to 25 MB</p>
          <input ref={fileRef} type="file" accept=".docx,.pdf,.md,.txt,.html,.markdown" className="hidden" onChange={(e) => importFile(e.target.files[0])} data-testid="import-file-input" />
          <button onClick={() => fileRef.current.click()} disabled={busy} className="mt-6 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 rounded-full border border-white/15 text-slate-300 hover:text-amber-300 hover:border-amber-400/50 disabled:opacity-50 transition-colors" data-testid="import-browse-btn">
            {busy ? 'Transcribing…' : 'Browse files'}
          </button>
        </div>

        <form onSubmit={importUrl} className={box} data-testid="import-url-form">
          <Link2 size={28} className="text-cyan-300" strokeWidth={1.5} />
          <p className="font-celestial text-lg text-slate-100 mt-4">Pull a Google Doc</p>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">The doc must be shared as <span className="text-slate-300">Anyone with the link → Viewer</span>. Headings, bold and lists are preserved.</p>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/document/d/…" className={`${inputCls} mt-5 font-mono-precision text-xs`} data-testid="import-url-input" />
          <button type="submit" disabled={busy || !url} className="mt-4 w-full font-mono-precision text-[10px] uppercase tracking-[0.25em] py-2.5 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#02040a] disabled:opacity-40 transition-colors" data-testid="import-url-submit">
            {busy ? 'Fetching…' : 'Import from URL'}
          </button>
        </form>
      </div>

      <div className={`${box} flex gap-4 items-start`}>
        <FileText size={18} className="text-slate-500 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500 leading-relaxed">Zero edits. The file's text is stored exactly as written — first line becomes the title, everything else is the body, character for character. Headings, quotes and arrow-chains are recognised only at display time. A word-for-word check runs on every import and shows as a “Verbatim” badge on the page.</p>
      </div>
    </div>
  );
}
